import { API_URL } from 'config';
import { makeAPICall } from './api';

export declare namespace API {
    export interface Group {
        id: number;
        name: string;
    }

    export interface Organisation {
        id: number;
        name: string;
    }

    export interface UserProfile {
        connection_id: string | null;
        allow_emails: boolean;
    }

    export interface UserLight {
        id: number;
        username: string;
        email: string;
        groups: Group[];
        first_name: string;
        last_name: string;
        is_active: boolean;
    }

    export interface User {
        id: number;
        username: string;
        email: string;
        groups: Group[];
        organisation_set: Organisation[];
        first_name: string;
        last_name: string;
        is_active: boolean;
        is_staff: boolean;
        is_superuser: boolean;
        profile: UserProfile;
    }

    export interface CommandResponse {
        success: boolean;
        error?: string;
    }
}

export type Group =
    | 'Organisation Manager'
    | 'Ground Station Operator'
    | 'Satellite Operator';

export interface UserProfile {
    connectionId: string | null;
    allowEmails: boolean;
}

export interface UserLight {
    id: number;
    fullName: string;
    userName: string;
    firstName: string;
    lastName: string;
    email: string;
    groups: Group[];
    isActive: boolean;
}

export interface User extends UserLight {
    profile: UserProfile;
    organisation_set: API.Organisation[];
    isStaff: boolean;
    isSuperuser: boolean;
}

const USERS_ROUTE = '/users';

export const mapAPIToUserLight = ({
    id,
    email,
    first_name,
    last_name,
    groups,
    is_active,
    username,
}: API.UserLight): UserLight => ({
    id,
    userName: username,
    fullName: `${first_name} ${last_name}`,
    firstName: first_name,
    lastName: last_name,
    email,
    groups: groups.map((g) => g.name as Group),
    isActive: is_active,
});

export const mapAPIToUser = ({
    id,
    email,
    first_name,
    last_name,
    groups,
    is_active,
    is_staff,
    is_superuser,
    organisation_set,
    username,
    profile,
}: API.User): User => ({
    id,
    userName: username,
    fullName: `${first_name} ${last_name}`,
    firstName: first_name,
    lastName: last_name,
    email,
    groups: groups.map((g) => g.name as Group),
    organisation_set,
    isActive: is_active,
    isStaff: is_staff,
    isSuperuser: is_superuser,
    profile: {
        connectionId: profile.connection_id,
        allowEmails: profile.allow_emails,
    },
});

export const listUsers = makeAPICall<User[], void, { results: API.User[] }>(
    () => ({
        url: `${API_URL}${USERS_ROUTE}/`,
    }),
    (data) => data.results.map(mapAPIToUser),
);

export const getSatelliteById = makeAPICall<User, { id: number }, API.User>(
    ({ id }) => ({
        url: `${API_URL}${USERS_ROUTE}/${id}/`,
    }),
    (satellite) => mapAPIToUser(satellite),
);
